import React, { FC } from 'react'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { GalleryCarousel } from '@/views/PreSales/PreSalesGiveawayView/components/Tabs/PresaleDetailsTab/GalleryCarousel'
import {
  GParagraphLG,
  GResponsiveText,
  GTitleLG,
  GTitleXL,
  GTitleXS,
} from '@/views/PreSales/PreSalesGiveawayView/components/UI/Typography'
import { useGiveawayContext } from '@/views/PreSales/PreSalesGiveawayView/context/GiveawayContext'

export const YoungWashingtonDetailsContent: FC = () => {
  const { t } = useTranslate('tickets')
  const { galleryImages, theatricalSlug, experiments } = useGiveawayContext()

  const isSingleTab = experiments?.['young-washington-single-tab']

  return (
    <div className="mt-0 grid gap-[72px]">
      <div>
        <Translate i18nKey={'youngWashingtonGiveawayDetailsAngel'} t={t}>
          <GParagraphLG color="giveaway-text-subtle-light" className={'mb-6 pt-3'}>
            Young Washington is a gripping new historical epic from Angel (Sound of Freedom), The Wonder Project, and
            acclaimed director Jon Erwin (Jesus Revolution, House of David).
          </GParagraphLG>
          <GParagraphLG color="giveaway-text-subtle-light" className={'mb-6 pt-3'}>
            Before he became a founding father, George Washington he was a flawed young soldier fighting to find his
            purpose.
          </GParagraphLG>
          <GParagraphLG color="giveaway-text-subtle-light" className={'mb-6 pt-3'}>
            Set against the frontier of colonial America, this film brings to life the untold origin story of a
            21-year-old George Washington—long before the Revolution, long before the Constitution—when a single mistake
            nearly cost him everything.
          </GParagraphLG>
          <GParagraphLG color="giveaway-text-subtle-light" className={'mb-6 pt-3'}>
            After triggering the start of the French and Indian War, Washington must reckon with personal failure,
            devastating loss, and the weight of responsibility. What emerges from the ashes isn’t just a military
            leader—but a man forged by humility, courage, and conviction.
          </GParagraphLG>
          <GParagraphLG color="giveaway-text-subtle-light" className={'mb-6 pt-3'}>
            In an era desperate for character-driven heroes, Young Washington is a bold reminder that greatness isn’t
            born—it’s built.
          </GParagraphLG>
          <GParagraphLG color="giveaway-text-subtle-light" className={'pt-3'}>
            Join us in making plans for NEXT July 4th—our country’s 250th birthday—to see a story that celebrates what
            America is really all about. Enjoy the celebration together with family and friends through this
            action-packed, emotionally resonant journey of leadership, legacy, and the blessing of liberty.
          </GParagraphLG>
        </Translate>
      </div>
      <div>
        <GResponsiveText
          color="giveaway-text-primary-dark"
          className="mb-3 uppercase"
          MobileTextComponent={GTitleLG}
          DesktopTextComponent={GTitleXL}
        >
          {t('whyYouShouldBuy1YearEarly', 'WHY YOU SHOULD BUY YOUR TICKETS 1 YEAR EARLY')}
        </GResponsiveText>
        <GParagraphLG color="giveaway-text-subtle-light" className={'mb-6 pt-3'}>
          {t(
            'earlyTicketSalesExplainer',
            'Early ticket sales determine how many theaters will carry the movie—and how many Americans we can reach.',
          )}
        </GParagraphLG>
        <GParagraphLG color="giveaway-text-subtle-light" className={'pt-3'}>
          {t(
            'earlyTicketSalesExplainerPt2',
            'Releasing on one of the most competitive box office weekends of the year means we need to show theaters that everyone in this country should be able to see this important story. By purchasing now, you’re not just reserving your seat. You’re helping reclaim our history, support value-driven storytelling, and show the world that these stories matter.',
          )}
        </GParagraphLG>
      </div>
      {galleryImages.length >= 1 && !isSingleTab && (
        <GalleryCarousel galleryImages={galleryImages} theatricalSlug={theatricalSlug} />
      )}
      <div>
        <GResponsiveText
          color="giveaway-text-primary-dark"
          className="mb-3 uppercase"
          MobileTextComponent={GTitleLG}
          DesktopTextComponent={GTitleXL}
        >
          {t('youngWashingtonGrandPrizeTitle', 'Young Washington Grand Prize Classic Truck Giveaway')}
        </GResponsiveText>
        <GParagraphLG color="giveaway-text-subtle-light" className={'mb-3 pt-2'}>
          {t(
            'youngWashingtonGrandPrizeDetails',
            'To celebrate the 250th anniversary of the United States and the premiere of Young Washington, we’re giving away a classic Ford F-250 truck (1975–1995 model, valued at $25,000) and a trip for two to Los Angeles for the red carpet premiere of Young Washington (airfare, hotel, transportation, and food stipend included).',
          )}
        </GParagraphLG>
        <GResponsiveText
          color="giveaway-text-subtle-light"
          className="pt-2"
          MobileTextComponent={GTitleXS}
          DesktopTextComponent={GTitleXS}
        >
          {t('giveawaySchedule', 'Premiere tentatively scheduled for late June 2026')}
        </GResponsiveText>
      </div>
      <div>
        <GResponsiveText
          color="giveaway-text-primary-dark"
          className="mb-3 uppercase"
          MobileTextComponent={GTitleLG}
          DesktopTextComponent={GTitleXL}
        >
          {t('youngWashingtonFirstPrizeTitleNumerical', '1st Place Prizes (4 Winners)')}
        </GResponsiveText>
        <GParagraphLG color="giveaway-text-subtle-light" className={'mb-6 pt-2'}>
          <Translate t={t} i18nKey="youngWashingtonFirstPrizeDetails">
            A trip for two to the Young Washington red carpet premiere, including airfare, two nights of hotel
            accommodations, transportation to and from the venue, and food stipend.+
          </Translate>
        </GParagraphLG>
      </div>
      <div>
        <GResponsiveText
          color="giveaway-text-primary-dark"
          className="mb-3 uppercase"
          MobileTextComponent={GTitleLG}
          DesktopTextComponent={GTitleXL}
        >
          {t('youngWashingtonSecondPrizeTitleNumerical', '2nd Place Prizes (1,000 Winners)')}
        </GResponsiveText>
        <GParagraphLG color="giveaway-text-subtle-light" className={'mb-6 pt-2'}>
          <Translate t={t} i18nKey="youngWashingtonSecondPrizeDetails">
            An exclusive limited-edition Young Washington hat, shipped straight to your door.
          </Translate>
        </GParagraphLG>
        <GParagraphLG color="giveaway-text-subtle-light" className={'mb-6 pt-3 !font-semibold'}>
          <Translate t={t} i18nKey="youngWashingtonDetailsDontWait">
            Don’t wait! Secure your tickets today to enter this one-of-a-kind giveaway and make history part of your
            future. Pre-order your tickets now to see Young Washington in theaters on July 4th, 2026!
          </Translate>
        </GParagraphLG>
      </div>
    </div>
  )
}
